<template>
  <!-- Renders no children and never renders itself — that is what caps quote depth at 1. -->
  <div
    v-if="quotedMessage && quotedMessage.state"
    class="v2026-qm"
    :class="accentClass"
    role="note"
    :aria-label="COPY.aria">

    <div class="v2026-qm__header" v-if="isFound">
      <!-- v-lazy, never :src or @load — a @load here would reach the bubble image counter and kill
           auto-scroll app-wide. vue-lazyload's global config already supplies the error image.
           Do not give it img-circle / image-profile: MainPanelV2026 ::v-deep-restyles those. -->
      <i class="fa fa-reply" style="color: #98a2b3;" aria-hidden="true"></i>
      <img
        class="v2026-qm__avatar"
        v-lazy="avatarSrc"
        alt=""
        aria-hidden="true" />
      <span
        class="v2026-qm__sender"
        :class="isAdminQuote ? 'v2026-qm__sender--admin' : 'v2026-qm__sender--customer'">{{ senderName }}</span>
      <span class="v2026-qm__time" v-if="timeText">{{ timeText }}</span>
    </div>

    <div class="v2026-qm__missing" v-if="!isFound">{{ COPY.notFound }}</div>

    <div class="v2026-qm__row" v-else>
      <!-- Fixed size in CSS and no @load handler, both required: a @load reaches the bubble image
           counter and kills auto-scroll app-wide, and an intrinsic size reflows after the scroll. -->
      <img
        v-if="showThumb"
        class="v2026-qm__thumb"
        :class="{ 'v2026-qm__thumb--sticker': kind === 'sticker' }"
        :src="quotedMessage.preview_url"
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
        @error="onThumbError" />
      <span class="v2026-qm__tile" v-else-if="tileGlyph" aria-hidden="true">
        <i :class="['fas', tileGlyph]"></i>
      </span>

      <div class="v2026-qm__detail">
        <div class="v2026-qm__label" v-if="kindLabel">{{ kindLabel }}</div>
        <!-- Interpolation, never v-html: no injection surface and no competing links inside. -->
        <div
          class="v2026-qm__body"
          :class="{ 'v2026-qm__body--one-line': kind !== 'text' }"
          v-if="bodyText"
          :title="bodyTitle">{{ bodyText }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

const COPY = {
  // Stands in for a missing sender name, so admin vs customer is never carried by colour alone.
  role: {
    admin: 'แอดมิน',
    customer: 'ลูกค้า'
  },
  notFound: 'ไม่พบข้อความต้นทาง',
  kind: {
    image: 'รูปภาพ',
    sticker: 'สติกเกอร์',
    video: 'วิดีโอ',
    audio: 'ข้อความเสียง',
    file: 'ไฟล์แนบ', // shown ONLY when the file name is unknown
    location: 'ตำแหน่งที่ตั้ง',
    system: 'ข้อความระบบ',
    unknown: 'ข้อความ' // terminal fallback + the empty-text fallback
  },
  aria: 'ข้อความที่ถูกตอบกลับ'
}

// Mirrors detectPreviewKind() in chat-server/helpers/quotedMessageHelper.js; unknown kinds degrade.
const KNOWN_KINDS = ['text', 'image', 'sticker', 'video', 'audio', 'file', 'location', 'system', 'unknown']

// `null` = this kind has no leading box at all.
const KIND_GLYPH = {
  text: null,
  image: 'fa-image',
  sticker: 'fa-face-smile',
  video: 'fa-play',
  audio: 'fa-microphone',
  file: 'fa-file-lines',
  location: 'fa-location-dot',
  system: 'fa-circle-info',
  unknown: 'fa-comment'
}

// Counted in CODE POINTS, never .length, so an emoji surrogate pair is never cut in half.
const TEXT_CAP = 80

export default {
  name: 'QuotedMessageV2026',
  props: {
    quotedMessage: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      thumbFailed: false
    }
  },
  computed: {
    COPY () {
      return COPY
    },
    isFound () {
      return !!this.quotedMessage && this.quotedMessage.state === 'found'
    },
    kind () {
      const raw = this.quotedMessage ? this.quotedMessage.preview_kind : null
      return KNOWN_KINDS.indexOf(raw) >= 0 ? raw : 'unknown'
    },
    isAdminQuote () {
      return this.isFound && this.quotedMessage.own === 1
    },
    // Never returns an empty string — the role word takes over, so colour is never the only signal.
    senderName () {
      if (!this.isFound) return ''
      const name = this.quotedMessage.sender_name
      if (name && String(name).trim()) return String(name).trim()
      return this.isAdminQuote ? COPY.role.admin : COPY.role.customer
    },
    avatarSrc () {
      if (!this.isFound) return null
      return this.quotedMessage.sender_avatar || null
    },
    timeText () {
      if (!this.isFound || !this.quotedMessage.created_at) return ''
      const parsed = moment(this.quotedMessage.created_at)
      if (!parsed.isValid()) return ''
      return this.$options.filters.chatDateTime(this.quotedMessage.created_at)
    },
    kindLabel () {
      if (!this.isFound) return ''
      if (this.kind === 'text') {
        return this.normalizedText ? '' : COPY.kind.unknown
      }
      if (this.kind === 'file') {
        // The glyph already says "file"; the prefix would eat ~5 characters of the name.
        return this.normalizedText ? '' : COPY.kind.file
      }
      return COPY.kind[this.kind] || COPY.kind.unknown
    },
    normalizedText () {
      const raw = this.isFound ? this.quotedMessage.preview_text : null
      if (raw === null || raw === undefined) return ''
      // Collapsing newlines is essential: a quoted 6-line message must occupy 2 lines, not 6.
      return String(raw).replace(/\s+/g, ' ').trim()
    },
    bodyText () {
      const text = this.normalizedText
      if (!text) return ''
      const chars = Array.from(text)
      if (chars.length <= TEXT_CAP) return text
      return chars.slice(0, TEXT_CAP).join('').trimEnd() + '…'
    },
    bodyTitle () {
      const text = this.normalizedText
      return (text && Array.from(text).length > TEXT_CAP) ? text : null
    },
    showThumb () {
      return (this.kind === 'image' || this.kind === 'sticker') &&
        this.isFound && !!this.quotedMessage.preview_url && !this.thumbFailed
    },
    tileGlyph () {
      return KIND_GLYPH[this.kind] || null
    },
    // Paints nothing since the accent bar was dropped; kept as a root state hook for selectors.
    accentClass () {
      if (!this.isFound) return 'v2026-qm--missing'
      return this.quotedMessage.own === 1 ? 'v2026-qm--admin' : 'v2026-qm--customer'
    }
  },
  methods: {
    onThumbError () {
      this.thumbFailed = true
    }
  }
}
</script>

<style scoped>
.v2026-qm {
  display: block;
  width: auto;
  max-width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  margin: 0 0 6px 0;
  border: 1px solid #D0D5DD;
  border-radius: 8px 8px 0 0;
  background: var(--v2026-surface-sunken, #F2F4F7);
  white-space: normal;
}

.v2026-qm__header {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.v2026-qm__avatar {
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  object-fit: cover;
  background: #E4E7EC;
}

.v2026-qm__sender {
  flex: 0 1 auto;
  min-width: 0;
  font-size: 13px;
  line-height: 18px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.v2026-qm__sender--customer {
  color: #344054;
}

.v2026-qm__sender--admin {
  color: #197280;
}

.v2026-qm__time {
  flex: 0 0 auto;
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
  white-space: nowrap;
  color: var(--v2026-text-secondary, #667085);
}

.v2026-qm__missing {
  font-size: 13px;
  line-height: 18px;
  color: var(--v2026-text-secondary, #667085);
  overflow-wrap: anywhere;
  word-break: break-word;
}

.v2026-qm__row {
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.v2026-qm__thumb,
.v2026-qm__tile {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  box-sizing: border-box;
}

.v2026-qm__thumb {
  object-fit: cover;
  border-radius: 6px;
  background: #E4E7EC;
}

.v2026-qm__thumb--sticker {
  object-fit: contain;
  border-radius: 0;
  background: transparent;
}

.v2026-qm__tile {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: #E9EBF0;
  font-size: 16px;
  line-height: 1;
  color: var(--v2026-text-secondary, #667085);
}

.v2026-qm__detail {
  min-width: 0;
}

.v2026-qm__label {
  font-size: 12px;
  line-height: 16px;
  color: var(--v2026-text-secondary, #667085);
}

.v2026-qm__body {
  font-size: 13px;
  line-height: 18px;
  color: #475467;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  max-height: 36px;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.v2026-qm__body--one-line {
  -webkit-line-clamp: 1;
  max-height: 18px;
}

@media (max-width: 820px) {
  .v2026-qm {
    padding: 6px 8px;
  }

  .v2026-qm__avatar {
    flex: 0 0 18px;
    width: 18px;
    height: 18px;
  }

  .v2026-qm__thumb,
  .v2026-qm__tile {
    flex: 0 0 32px;
    width: 32px;
    height: 32px;
  }

  .v2026-qm__tile {
    font-size: 14px;
  }

  .v2026-qm__row {
    gap: 6px;
  }

  .v2026-qm__time {
    display: none;
  }
}
</style>
